import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DragDotsIconComponent, ResetIconComponent } from '@kuiper/icons';
import { SelectComponent } from '../select/select.component';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import { baseUrl } from '../../../core/common/constants/endpoints.constants';
import { OptionItem } from '../option.interface';
import { LookupOptionItem } from '../lookup-field/lookup-field-item';
import { PaginatedSelectComponent } from '../paginated-select/paginated-select.component';
import {
  AssigneeDropdownConfig,
  RESOURCE_TYPE_CONFIG,
  ResourceTypeConfig,
} from './resource-type.model';

@Component({
  selector: 'lib-resource-type',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    SelectComponent,
    MultiSelectComponent,
    PaginatedSelectComponent,
    DragDotsIconComponent,
    ResetIconComponent,
  ],
  templateUrl: './resource-type.component.html',
  styleUrl: './resource-type.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ResourceTypeComponent implements OnInit {
  private translocoService = inject(TranslocoService);
  private fb = inject(FormBuilder);

  // Input for restoring previously applied filter values
  initialValue = input<string>('');

  // Optional types to use instead of the built-in resource types, provided by the page
  private readonly providedConfig = inject(RESOURCE_TYPE_CONFIG, {
    optional: true,
  });
  readonly config = signal<ResourceTypeConfig | undefined>(
    this.providedConfig ?? undefined
  );

  // Outputs for filter values
  filterChange = output<{
    resourceType: string;
    assigneeId: (string | number)[];
    assigneeOptions: { value: string | number; label: string }[];
  }>();
  filterReset = output<void>();

  baseUrl = baseUrl;

  // Form for the filter
  filterForm = signal<FormGroup>(
    this.fb.group({
      resourceType: ['', Validators.required],
      assigneeId: [{ value: [], disabled: true }, Validators.required],
      assigneeSingleId: [{ value: null, disabled: true }],
    })
  );

  // Selected options
  selectedResourceTypeOption = signal<OptionItem | undefined>(undefined);
  selectedAssigneeOptions = signal<OptionItem[]>([]);

  // Built-in resource type options
  private readonly defaultResourceTypeOptions: OptionItem[] = [
    {
      label: this.translocoService.translate('request_screen_department_label'),
      value: 'department',
    },
    {
      label: this.translocoService.translate('request_screen_group_label'),
      value: 'group',
    },
    {
      label: this.translocoService.translate(
        'request_screen_legal_entity_label'
      ),
      value: 'legal_entity',
    },
    {
      label: this.translocoService.translate('request_screen_user_label'),
      value: 'user',
    },
  ];

  readonly resourceTypeOptions = computed<OptionItem[]>(() => {
    const config = this.config();
    if (!config) return this.defaultResourceTypeOptions;

    return config.types.map(type => ({
      label: this.translocoService.translate(type.label),
      value: type.value,
    }));
  });

  readonly isMultiple = computed(() => this.config()?.multiple ?? true);
  readonly selectedSingleAssigneeOption = computed(
    () => this.selectedAssigneeOptions()[0] as LookupOptionItem | undefined
  );

  readonly typeLabel = computed(
    () => this.config()?.typeLabel ?? 'filter_resources_type_label'
  );
  readonly typePlaceholder = computed(
    () => this.config()?.typePlaceholder ?? 'filter_resources_type_placeholder'
  );
  readonly assigneeLabel = computed(
    () => this.config()?.assigneeLabel ?? 'filter_dependable_assignee_label'
  );
  readonly assigneePlaceholder = computed(
    () =>
      this.config()?.assigneePlaceholder ??
      'filter_dependable_assignee_placeholder'
  );

  // Assignee dropdown dependency configuration
  assigneeDropdownConfig = signal<AssigneeDropdownConfig>({
    api: '',
    placeholder: this.translocoService.translate(
      'request_screen_select_resource_placeholder'
    ),
    filters: [],
  });

  ngOnInit(): void {
    const initial = this.initialValue();
    if (initial) {
      this.restoreFromInitialValue(initial);
    }
  }

  private restoreFromInitialValue(initial: string): void {
    try {
      const parsed = JSON.parse(initial);
      if (!parsed.resourceType) return;

      const resourceTypeOption = this.resourceTypeOptions().find(
        opt => opt.value === parsed.resourceType
      );
      if (!resourceTypeOption) return;

      this.selectedResourceTypeOption.set(resourceTypeOption);
      this.filterForm().patchValue(
        { resourceType: parsed.resourceType },
        { emitEvent: false }
      );
      this.setAssigneeEnabled(true);
      this.assigneeDropdownConfig.set(
        this.getAssigneeConfig(parsed.resourceType)
      );

      if (parsed.assigneeId && parsed.assigneeId.length > 0) {
        this.filterForm()
          .get('assigneeId')
          ?.setValue(parsed.assigneeId, { emitEvent: false });
        this.filterForm()
          .get('assigneeSingleId')
          ?.setValue(parsed.assigneeId[0], { emitEvent: false });

        if (parsed.assigneeOptions && parsed.assigneeOptions.length > 0) {
          this.selectedAssigneeOptions.set(parsed.assigneeOptions);
        }
      }
    } catch {
      // Invalid JSON, ignore
    }
  }

  /**
   * Handle resource type dropdown change
   */
  onResourceTypeChange(event: OptionItem): void {
    const type = String(event.value);
    if (!type || type === this.selectedResourceTypeOption()?.value) return;

    this.selectedResourceTypeOption.set(event);
    this.filterForm().patchValue(
      {
        resourceType: type,
        assigneeId: [],
        assigneeSingleId: null,
      },
      { emitEvent: false }
    );

    this.selectedAssigneeOptions.set([]);
    this.setAssigneeEnabled(true);
    this.assigneeDropdownConfig.set(this.getAssigneeConfig(type));
    this.emitFilterChange();
  }

  /**
   * Handle assignee dropdown change (multi-select)
   */
  onAssigneeChange(selectedOptions: OptionItem[]): void {
    this.selectedAssigneeOptions.set(selectedOptions);
    // Store IDs in the form control
    const assigneeIds = selectedOptions.map(
      option => option.value || option.id
    );
    this.filterForm()
      .get('assigneeId')
      ?.setValue(assigneeIds, { emitEvent: false });

    // Emit filter change
    this.emitFilterChange();
  }

  /**
   * Handle assignee dropdown change (single select)
   */
  onSingleAssigneeChange(option: LookupOptionItem): void {
    const hasOption = option?.value !== undefined && option?.value !== null;
    this.selectedAssigneeOptions.set(
      hasOption ? [{ value: option.value, label: option.label }] : []
    );
    this.filterForm()
      .get('assigneeId')
      ?.setValue(hasOption ? [option.value] : [], { emitEvent: false });

    this.emitFilterChange();
  }

  /**
   * Clear the filter
   */
  clearFilter(): void {
    this.filterForm().reset();
    this.selectedResourceTypeOption.set(undefined);
    this.selectedAssigneeOptions.set([]);
    this.setAssigneeEnabled(false);
    this.assigneeDropdownConfig.set({
      api: '',
      placeholder: this.translocoService.translate(
        'request_screen_select_resource_placeholder'
      ),
      filters: [],
    });
    this.filterReset.emit();
  }

  /**
   * Clear resource type field
   */
  clearResourceType(): void {
    this.filterForm().patchValue({
      resourceType: '',
      assigneeId: [],
      assigneeSingleId: null,
    });
    this.selectedResourceTypeOption.set(undefined);
    this.selectedAssigneeOptions.set([]);
    this.setAssigneeEnabled(false);
    this.assigneeDropdownConfig.set({
      api: '',
      placeholder: this.translocoService.translate(
        'request_screen_select_resource_placeholder'
      ),
      filters: [],
    });
    this.filterReset.emit();
  }

  /**
   * Clear assignee field
   */
  clearAssignee(): void {
    this.filterForm().patchValue({
      assigneeId: [],
      assigneeSingleId: null,
    });
    this.selectedAssigneeOptions.set([]);
    this.emitFilterChange();
  }

  /**
   * Emit filter change event
   */
  private emitFilterChange(): void {
    const formValue = this.filterForm().getRawValue();
    if (formValue.resourceType) {
      this.filterChange.emit({
        resourceType: formValue.resourceType,
        assigneeId: formValue.assigneeId || [],
        assigneeOptions: this.selectedAssigneeOptions().map(opt => ({
          value: (opt.value || opt.id || '') as string | number,
          label: String(opt.label || opt.name || ''),
        })),
      });
    }
  }

  private setAssigneeEnabled(enabled: boolean): void {
    ['assigneeId', 'assigneeSingleId'].forEach(key => {
      const control = this.filterForm().get(key);
      if (enabled) {
        control?.enable();
      } else {
        control?.disable();
      }
    });
  }

  /**
   * Get assignee dropdown configuration based on resource type
   */
  private getAssigneeConfig(resourceType: string): AssigneeDropdownConfig {
    const configuredType = this.config()?.types.find(
      type => type.value === resourceType
    );
    if (configuredType) {
      return {
        api: configuredType.api,
        placeholder: this.translocoService.translate(
          configuredType.placeholder
        ),
        filters: configuredType.filters ?? [{ key: 'search', value: '' }],
      };
    }

    const baseApiUrl = `${baseUrl}/lookup`;
    const defaultFilters = [{ key: 'search', value: '' }];
    const activeStateFilter = [{ key: 'state', value: 'active' }];

    switch (resourceType) {
      case 'department':
        return {
          api: `${baseApiUrl}/departments`,
          placeholder: this.translocoService.translate(
            'request_screen_select_department_placeholder'
          ),
          filters: [...defaultFilters, ...activeStateFilter],
        };
      case 'group':
        return {
          api: `${baseApiUrl}/groups`,
          placeholder: this.translocoService.translate(
            'request_screen_select_group_placeholder'
          ),
          filters: [...defaultFilters, ...activeStateFilter],
        };
      case 'legal_entity':
        return {
          api: `${baseApiUrl}/legal-entities`,
          placeholder: this.translocoService.translate(
            'request_screen_select_legal_entity_placeholder'
          ),
          filters: [...defaultFilters, ...activeStateFilter],
        };
      case 'user':
      default:
        return {
          api: `${baseApiUrl}/users?filters[not_state]=inactive`,
          placeholder: this.translocoService.translate(
            'request_screen_select_user_placeholder'
          ),
          filters: [...defaultFilters],
        };
    }
  }
}
